import { ethers } from "ethers";
import sample from "lodash/sample";

import {
  AnyChainId,
  ARBITRUM_SEPOLIA,
  BOTANIX,
  ContractsChainId,
  CONTRACTS_CHAIN_IDS as SDK_CONTRACTS_CHAIN_IDS,
  CONTRACTS_CHAIN_IDS_DEV as SDK_CONTRACTS_CHAIN_IDS_DEV,
  SOURCE_BASE_MAINNET,
  SOURCE_BSC_MAINNET,
  SOURCE_OPTIMISM_SEPOLIA,
  SOURCE_SEPOLIA,
} from "sdk/configs/chains";

import { isDevelopment } from "./env";
import { ARBITRUM, AVALANCHE, AVALANCHE_FUJI, ETH_MAINNET } from "./static/chains";

export { CHAIN_NAMES_MAP, getChainName } from "sdk/configs/chains";
export * from "./static/chains";

export const CONTRACTS_CHAIN_IDS = isDevelopment() ? SDK_CONTRACTS_CHAIN_IDS_DEV : SDK_CONTRACTS_CHAIN_IDS;

const { parseEther } = ethers;

export const ENV_ARBITRUM_RPC_URLS = import.meta.env.VITE_APP_ARBITRUM_RPC_URLS;
export const ENV_AVALANCHE_RPC_URLS = import.meta.env.VITE_APP_AVALANCHE_RPC_URLS;
export const ENV_BOTANIX_RPC_URLS = import.meta.env.VITE_APP_BOTANIX_RPC_URLS;
export const ENV_ALCHEMY_KEY = import.meta.env.VITE_APP_ALCHEMY_KEY;

// TODO take it from web3
export const DEFAULT_CHAIN_ID = ARBITRUM;
export const CHAIN_ID = DEFAULT_CHAIN_ID;

export const IS_NETWORK_DISABLED: Record<ContractsChainId, boolean> = {
  [ARBITRUM]: false,
  [AVALANCHE]: false,
  [ARBITRUM_SEPOLIA]: false,
  [AVALANCHE_FUJI]: false,
  [BOTANIX]: false,
};

export const NETWORK_EXECUTION_TO_CREATE_FEE_FACTOR = {
  [ARBITRUM]: 10n ** 29n * 5n,
  [AVALANCHE]: 10n ** 29n * 35n,
  [AVALANCHE_FUJI]: 10n ** 29n * 2n,
} as const;

const constants = {
  [ARBITRUM]: {
    nativeTokenSymbol: "ETH",
    wrappedTokenSymbol: "WETH",
    defaultCollateralSymbol: "USDC.e",
    defaultFlagOrdersEnabled: false,
    positionReaderPropsLength: 9,
    v2: true,

    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.0003"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.0003"),
    // contract requires that execution fee be strictly greater than instead of gte
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.000300001"),
  },

  [AVALANCHE]: {
    nativeTokenSymbol: "AVAX",
    wrappedTokenSymbol: "WAVAX",
    defaultCollateralSymbol: "USDC",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: true,

    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.01"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.01"),
    // contract requires that execution fee be strictly greater than instead of gte
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.0100001"),
  },

  [AVALANCHE_FUJI]: {
    nativeTokenSymbol: "AVAX",
    wrappedTokenSymbol: "WAVAX",
    defaultCollateralSymbol: "USDC",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: true,

    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.01"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.01"),
    // contract requires that execution fee be strictly greater than instead of gte
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.0100001"),
  },

  [ARBITRUM_SEPOLIA]: {
    nativeTokenSymbol: "ETH",
    wrappedTokenSymbol: "WETH",
    defaultCollateralSymbol: "USDC",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: true,

    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.01"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.01"),
    // contract requires that execution fee be strictly greater than instead of gte
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.0100001"),
  },
  [BOTANIX]: {
    nativeTokenSymbol: "BTC",
    wrappedTokenSymbol: "PBTC",
    defaultCollateralSymbol: "USDC.E",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: true,

    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.01"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.01"),
    // contract requires that execution fee be strictly greater than instead of gte
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.0100001"),
  },
} satisfies Record<ContractsChainId, Record<string, any>>;

export const RPC_PROVIDERS: Record<AnyChainId | typeof ETH_MAINNET, string[]> = {
  [ETH_MAINNET]: ["https://rpc.ankr.com/eth"],
  [ARBITRUM]: [
    "https://arb1.arbitrum.io/rpc",
    "https://arbitrum-one-rpc.publicnode.com",
    // "https://1rpc.io/arb", has CORS issue
    "https://arbitrum-one.public.blastapi.io",
    // "https://arbitrum.drpc.org",
    "https://rpc.ankr.com/arbitrum",
  ],
  [AVALANCHE]: [
    "https://api.avax.network/ext/bc/C/rpc",
    "https://avalanche-c-chain-rpc.publicnode.com",
    "https://1rpc.io/avax/c",
  ],
  [AVALANCHE_FUJI]: [
    "https://avalanche-fuji-c-chain.publicnode.com",
    "https://api.avax-test.network/ext/bc/C/rpc",
    // "https://ava-testnet.public.blastapi.io/v1/avax/fuji/public",
    // "https://rpc.ankr.com/avalanche_fuji",
  ],
  [ARBITRUM_SEPOLIA]: [
    "https://sepolia-rollup.arbitrum.io/rpc",
    "https://arbitrum-sepolia.drpc.org",
    "https://arbitrum-sepolia-rpc.publicnode.com",
  ],
  [SOURCE_BASE_MAINNET]: [
    "https://mainnet.base.org",
    "https://base.llamarpc.com",
    "https://base-rpc.publicnode.com",
    "https://base.drpc.org",
  ],
  [SOURCE_OPTIMISM_SEPOLIA]: [
    "https://sepolia.optimism.io",
    "https://optimism-sepolia.drpc.org",
    "https://optimism-sepolia.therpc.io",
  ],
  [SOURCE_SEPOLIA]: ["https://sepolia.drpc.org"],
  [BOTANIX]: [
    // returns incorrect gas price
    // "https://rpc.botanixlabs.com",
    "https://rpc.ankr.com/botanix_mainnet",
  ],
  [SOURCE_BSC_MAINNET]: [
    "https://bsc-dataseed.bnbchain.org",
    "https://1rpc.io/bnb",
    "https://bsc.drpc.org",
    "https://bsc-rpc.publicnode.com",
  ],
};

// Declared here, above the RPC tables below, rather than next to `getAlchemyKey`: those tables call
// `getAlchemy*HttpUrl` while this module is still evaluating, so a `let` declared further down would
// still be in its temporal dead zone and reading it would throw before the app can mount.
let didWarnAboutMissingAlchemyKey = false;

/**
 * Alchemy URLs are `undefined` when `VITE_APP_ALCHEMY_KEY` is unset. Substituting the chain's public
 * endpoints keeps these module-level tables well-formed so the app boots without the key.
 */
function withPublicRpcFallback(chainId: AnyChainId, alchemyHttpUrl: string | undefined): string[] {
  return alchemyHttpUrl ? [alchemyHttpUrl] : RPC_PROVIDERS[chainId];
}

export const FALLBACK_PROVIDERS: Record<AnyChainId, string[]> = {
  [ARBITRUM]: ENV_ARBITRUM_RPC_URLS
    ? JSON.parse(ENV_ARBITRUM_RPC_URLS)
    : withPublicRpcFallback(ARBITRUM, getAlchemyArbitrumHttpUrl("fallback")),
  [AVALANCHE]: ENV_AVALANCHE_RPC_URLS
    ? JSON.parse(ENV_AVALANCHE_RPC_URLS)
    : withPublicRpcFallback(AVALANCHE, getAlchemyAvalancheHttpUrl("fallback")),
  [AVALANCHE_FUJI]: [
    "https://endpoints.omniatech.io/v1/avax/fuji/public",
    "https://api.avax-test.network/ext/bc/C/rpc",
    "https://ava-testnet.public.blastapi.io/ext/bc/C/rpc",
  ],
  [BOTANIX]: ENV_BOTANIX_RPC_URLS
    ? JSON.parse(ENV_BOTANIX_RPC_URLS)
    : withPublicRpcFallback(BOTANIX, getAlchemyBotanixHttpUrl("fallback")),
  [ARBITRUM_SEPOLIA]: withPublicRpcFallback(ARBITRUM_SEPOLIA, getAlchemyArbitrumSepoliaHttpUrl("fallback")),
  [SOURCE_BASE_MAINNET]: withPublicRpcFallback(SOURCE_BASE_MAINNET, getAlchemyBaseMainnetHttpUrl("fallback")),
  [SOURCE_OPTIMISM_SEPOLIA]: withPublicRpcFallback(
    SOURCE_OPTIMISM_SEPOLIA,
    getAlchemyOptimismSepoliaHttpUrl("fallback")
  ),
  [SOURCE_SEPOLIA]: withPublicRpcFallback(SOURCE_SEPOLIA, getAlchemySepoliaHttpUrl("fallback")),
  // Keep BSC fallback on public RPCs to avoid Alchemy network/whitelist coupling.
  [SOURCE_BSC_MAINNET]: [
    "https://bsc-dataseed.bnbchain.org",
    "https://bsc-rpc.publicnode.com",
    "https://bsc.drpc.org",
  ],
};

export const PRIVATE_RPC_PROVIDERS: Partial<Record<AnyChainId, string[]>> = {
  [ARBITRUM]: withPublicRpcFallback(ARBITRUM, getAlchemyArbitrumHttpUrl("largeAccount")),
  [AVALANCHE]: withPublicRpcFallback(AVALANCHE, getAlchemyAvalancheHttpUrl("largeAccount")),
  [BOTANIX]: withPublicRpcFallback(BOTANIX, getAlchemyBotanixHttpUrl("largeAccount")),
  [SOURCE_BASE_MAINNET]: withPublicRpcFallback(SOURCE_BASE_MAINNET, getAlchemyBaseMainnetHttpUrl("largeAccount")),
};

export const EXPRESS_RPC_PROVIDERS: Partial<Record<AnyChainId, string[]>> = {
  [ARBITRUM]: withPublicRpcFallback(ARBITRUM, getAlchemyArbitrumHttpUrl("express")),
  [AVALANCHE]: withPublicRpcFallback(AVALANCHE, getAlchemyAvalancheHttpUrl("express")),
  [BOTANIX]: withPublicRpcFallback(BOTANIX, getAlchemyBotanixHttpUrl("express")),
  [SOURCE_BASE_MAINNET]: withPublicRpcFallback(SOURCE_BASE_MAINNET, getAlchemyBaseMainnetHttpUrl("express")),
  [ARBITRUM_SEPOLIA]: withPublicRpcFallback(ARBITRUM_SEPOLIA, getAlchemyArbitrumSepoliaHttpUrl("express")),
};

type ConstantName = keyof (typeof constants)[ContractsChainId];

export const getConstant = <T extends ContractsChainId, K extends ConstantName>(
  chainId: T,
  key: K
): (typeof constants)[T][K] => {
  if (!constants[chainId]) {
    throw new Error(`Unsupported chainId ${chainId}`);
  }

  if (!(key in constants[chainId])) {
    throw new Error(`Key ${key} does not exist for chainId ${chainId}`);
  }

  return constants[chainId][key];
};

export function getFallbackRpcUrl(chainId: number, isLargeAccount: boolean): string {
  return sample(isLargeAccount ? PRIVATE_RPC_PROVIDERS[chainId] : FALLBACK_PROVIDERS[chainId]);
}

export function getExpressRpcUrl(chainId: number): string {
  return sample(EXPRESS_RPC_PROVIDERS[chainId]);
}

type AlchemyKeyPurpose = "fallback" | "largeAccount" | "express";

export const hasAlchemyKey = Boolean(ENV_ALCHEMY_KEY);

/**
 * Returns the Alchemy key, or `undefined` when `VITE_APP_ALCHEMY_KEY` is unset. Callers must treat a
 * missing key as "this endpoint is unavailable" and fall back to the public RPCs in `RPC_PROVIDERS`,
 * so a local checkout without secrets still boots instead of throwing while this module evaluates.
 */
function getAlchemyKey(purpose: AlchemyKeyPurpose): string | undefined {
  if (!ENV_ALCHEMY_KEY) {
    if (!didWarnAboutMissingAlchemyKey) {
      didWarnAboutMissingAlchemyKey = true;
      // eslint-disable-next-line no-console
      console.warn(
        `VITE_APP_ALCHEMY_KEY is not set — falling back to public RPCs (first missed purpose: ${purpose}). ` +
          `Public endpoints are rate limited; set the key in .env for full functionality.`
      );
    }

    return undefined;
  }

  return ENV_ALCHEMY_KEY;
}

function alchemyUrl(template: (key: string) => string, purpose: AlchemyKeyPurpose): string | undefined {
  const key = getAlchemyKey(purpose);

  return key === undefined ? undefined : template(key);
}

export function getAlchemyArbitrumHttpUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `https://arb-mainnet.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemyAvalancheHttpUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `https://avax-mainnet.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemyArbitrumWsUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `wss://arb-mainnet.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemyBotanixHttpUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `https://botanix-mainnet.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemyBotanixWsUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `wss://botanix-mainnet.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemyOptimismSepoliaHttpUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `https://opt-sepolia.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemyOptimismSepoliaWsUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `wss://opt-sepolia.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemyArbitrumSepoliaHttpUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `https://arb-sepolia.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemyArbitrumSepoliaWsUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `wss://arb-sepolia.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemyBaseMainnetHttpUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `https://base-mainnet.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemyBaseMainnetWsUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `wss://base-mainnet.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemyBscMainnetHttpUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `https://bnb-mainnet.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemyBscMainnetWsUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `wss://bnb-mainnet.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemySepoliaHttpUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `https://eth-sepolia.g.alchemy.com/v2/${key}`, purpose);
}

export function getAlchemySepoliaWsUrl(purpose: AlchemyKeyPurpose) {
  return alchemyUrl((key) => `wss://eth-sepolia.g.alchemy.com/v2/${key}`, purpose);
}

export function getExplorerUrl(chainId: number | "layerzero" | "layerzero-testnet"): string {
  switch (chainId as AnyChainId | "layerzero" | "layerzero-testnet") {
    case ARBITRUM:
      return "https://arbiscan.io/";
    case AVALANCHE:
      return "https://snowtrace.io/";
    case AVALANCHE_FUJI:
      return "https://testnet.snowtrace.io/";
    case ARBITRUM_SEPOLIA:
      return "https://sepolia.arbiscan.io/";
    case SOURCE_OPTIMISM_SEPOLIA:
      return "https://sepolia-optimism.etherscan.io/";
    case SOURCE_SEPOLIA:
      return "https://sepolia.etherscan.io/";
    case BOTANIX:
      return "https://botanixscan.io/";
    case SOURCE_BASE_MAINNET:
      return "https://basescan.org/";
    case SOURCE_BSC_MAINNET:
      return "https://bscscan.com/";
    case "layerzero":
      return "https://layerzeroscan.com/";
    case "layerzero-testnet":
      return "https://testnet.layerzeroscan.com/";
  }
}

export function getTokenExplorerUrl(chainId: number, tokenAddress: string) {
  return `${getExplorerUrl(chainId)}token/${tokenAddress}`;
}
